import {
  useDairyEntries,
  useDairyLastEntry,
  useDairyPreviousDay,
  useRegisterBatchEvent,
  type DairyEntry,
} from '@/api/hooks';
import { DairySubnav } from '@/components/dairy/DairySubnav';
import { MilkWeighingFormModal } from '@/components/dairy/MilkWeighingFormModal';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { ListPagination } from '@/components/ui/ListPagination';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { getErrorMessage } from '@/lib/api';
import { formatLiters, milkingShiftLabel } from '@/lib/dairy';
import { formatDate, todayIso } from '@/lib/format';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

export function DairyEntriesPage() {
  const { propertyId } = usePropertyScope();

  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [initialEntry, setInitialEntry] = useState<DairyEntry | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const filters = useMemo(
    () => ({ property_id: propertyId ?? undefined, page }),
    [propertyId, page],
  );
  const { data, isLoading } = useDairyEntries(filters);
  const lastEntry = useDairyLastEntry(propertyId);
  const previousDay = useDairyPreviousDay(propertyId);
  const registerBatch = useRegisterBatchEvent();

  async function handleDuplicate() {
    setError('');
    setMessage('');
    try {
      const res = await lastEntry.mutateAsync(undefined);
      if (!res.entry) {
        setError('Nenhum lançamento anterior encontrado.');
        return;
      }
      setInitialEntry(res.entry);
      setOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao buscar último lançamento.'));
    }
  }

  async function handleCopyPreviousDay() {
    setError('');
    setMessage('');
    if (!propertyId) return;
    try {
      const res = await previousDay.mutateAsync();
      if (res.items.length === 0) {
        setError('Não há lançamentos no dia anterior.');
        return;
      }

      // Group by shift and register batches
      const byShift = new Map<string, DairyEntry[]>();
      for (const item of res.items) {
        const key = item.milking_shift || 'morning';
        const list = byShift.get(key) ?? [];
        list.push(item);
        byShift.set(key, list);
      }

      let total = 0;
      for (const [shift, items] of byShift) {
        const result = await registerBatch.mutateAsync({
          event_type: 'milk_weighing',
          property_id: propertyId,
          event_date: todayIso(),
          milking_shift: shift,
          items: items.map((i) => ({
            animal_id: i.cow_id,
            production_liters: i.production_liters,
          })),
        });
        total += result.registered_count;
      }
      setMessage(`${total} lançamento(s) copiados do dia ${formatDate(res.source_date)}.`);
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao copiar produção do dia anterior.'));
    }
  }

  return (
    <div>
      <PageHeader
        title="Lançamentos de leite"
        subtitle="Pesagens por animal e turno"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDuplicate}
              loading={lastEntry.isPending}
              disabled={!propertyId}
            >
              Duplicar anterior
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyPreviousDay}
              loading={previousDay.isPending || registerBatch.isPending}
              disabled={!propertyId}
            >
              Copiar dia anterior
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setInitialEntry(null);
                setOpen(true);
              }}
              disabled={!propertyId}
            >
              Novo lançamento
            </Button>
          </div>
        }
      />
      <DairySubnav />

      {message && (
        <Alert variant="success" className="mb-4">
          {message}
        </Alert>
      )}
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {!propertyId ? (
        <Card>
          <CardBody className="py-10 text-center text-manejai-bark/60">Selecione uma propriedade.</CardBody>
        </Card>
      ) : isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 rounded-xl bg-manejai-sand/40 animate-pulse" />
          ))}
        </div>
      ) : (data?.entries.length ?? 0) === 0 ? (
        <Card>
          <CardBody className="py-12 text-center text-manejai-bark/60">
            Nenhum lançamento ainda.{' '}
            <button type="button" className="text-manejai-moss underline" onClick={() => setOpen(true)}>
              Registrar o primeiro
            </button>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/55">
                    <th className="px-4 py-3 font-medium">Data</th>
                    <th className="px-4 py-3 font-medium">Animal</th>
                    <th className="px-4 py-3 font-medium">Turno</th>
                    <th className="px-4 py-3 font-medium">Produção</th>
                    <th className="px-4 py-3 font-medium">Diária</th>
                    <th className="px-4 py-3 font-medium">Lactação</th>
                    <th className="px-4 py-3 font-medium">DEL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-manejai-sand/50">
                  {data!.entries.map((row) => (
                    <tr key={row.event_id} className="hover:bg-manejai-cream/30">
                      <td className="px-4 py-3">{formatDate(row.event_date)}</td>
                      <td className="px-4 py-3">
                        <Link to={`/animais/${row.cow_id}`} className="text-manejai-moss hover:underline">
                          {row.internal_number}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{milkingShiftLabel(row.milking_shift)}</td>
                      <td className="px-4 py-3 font-medium">{formatLiters(row.production_liters)}</td>
                      <td className="px-4 py-3">{formatLiters(row.daily_liters)}</td>
                      <td className="px-4 py-3">{row.lactation_number ?? '—'}</td>
                      <td className="px-4 py-3">{row.del ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <ListPagination
            page={page}
            lastPage={data?.meta.last_page ?? 1}
            onPageChange={setPage}
            className="mt-4"
          />
        </>
      )}

      {propertyId && (
        <MilkWeighingFormModal
          open={open}
          onClose={() => {
            setOpen(false);
            setInitialEntry(null);
          }}
          propertyId={propertyId}
          initialEntry={initialEntry}
        />
      )}
    </div>
  );
}
